import { useState } from 'react';
import { upperFirst } from 'es-toolkit';

import {
  Box,
  Button,
  FormField,
  FormHelp,
  Icon,
  Input,
  Label,
  LabeledInput,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconMailFilled, IconPhoneCircleFilled } from '@zillow/constellation-icons';

import numberUtils from '../../../../utils/numberUtils';

import ListCell from './ListCell';
import PropertyLookupError from './PropertyLookupError';

import type React from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const lookupMap: Record<
  string,
  {
    text: { label: string; subheading: string; cta: string };
    icon: React.ReactNode;
    type: string;
    claimMethodsKey: 'emails' | 'phones';
  }
> = {
  phone: {
    text: {
      label: 'phone number',
      subheading: 'You’ll receive a verification code at this number',
      cta: 'Look up by email address instead',
    },
    icon: (
      <Icon size="lg" css={{ color: 'icon.accent.blue.strong' }}>
        <IconPhoneCircleFilled />
      </Icon>
    ),
    type: 'tel',
    claimMethodsKey: 'phones',
  },
  email: {
    text: {
      label: 'email',
      subheading: 'You’ll receive a verification link at this email',
      cta: 'Look up by phone number instead',
    },
    icon: (
      <Icon size="lg" css={{ color: 'icon.accent.blue.strong' }}>
        <IconMailFilled />
      </Icon>
    ),
    type: 'email',
    claimMethodsKey: 'emails',
  },
};

export type ContactMethodLookupProps = {
  onSwitchContactMethods: () => void;
  onSearchListings: () => void;
  setContactValue: (value: string) => void;
  contactValue: string;
  contactMethod: 'phone' | 'email' | '';
  contactMethods?: { emails: Array<{ value: string }>; phones: Array<{ value: string }> };
};

const ContactMethodLookup = ({
  onSwitchContactMethods,
  onSearchListings,
  setContactValue,
  contactValue,
  contactMethod,
  contactMethods = { emails: [], phones: [] },
}: ContactMethodLookupProps) => {
  const [error, setError] = useState('');
  const [propertyLookupError, setPropertyLookupError] = useState('');
  const [isValidContactMethodType, setIsValidContactMethodType] = useState(false);

  const config = lookupMap[contactMethod];
  if (!config) return null;

  const DUPLICATE_CONTACT_METHOD_ERROR = `You’ve already added this ${config.text.label}`;
  const INVALID_CONTACT_METHOD_ERROR = `Enter a valid ${config.text.label}`;

  const lookupListings = () => {
    const existing = contactMethods[config.claimMethodsKey];
    const duplicate = existing?.some((c) => c.value === contactValue);
    if (duplicate) {
      setError(DUPLICATE_CONTACT_METHOD_ERROR);
      return;
    }
    setError('');
    setPropertyLookupError('');
    onSearchListings();
  };

  const onBlurHandler = () => {
    setError(isValidContactMethodType ? '' : INVALID_CONTACT_METHOD_ERROR);
  };
  const onFocusHandler = () => setError('');
  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isValidContactMethodType && !error) lookupListings();
  };
  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (error) setError('');

    if (contactMethod === 'phone') {
      const cleanPhoneNumber = numberUtils.cleanPhoneNumber(value);
      setContactValue(cleanPhoneNumber);
      setIsValidContactMethodType(cleanPhoneNumber.length === 10);
      return;
    }
    setContactValue(value);
    setIsValidContactMethodType(EMAIL_REGEX.test(value));
  };

  return (
    <>
      <Box css={{ textAlign: 'center', marginTop: '400', marginBottom: '400' }}>
        <Paragraph textStyle="body-lg-bold">{`Enter the contact ${config.text.label} on your listing`}</Paragraph>
        <Paragraph>{config.text.subheading}</Paragraph>
        <TextButton onClick={onSwitchContactMethods}>{config.text.cta}</TextButton>
      </Box>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
        <Box css={{ gridColumnStart: { base: '1', lg: '3' }, gridColumnEnd: { base: 'span 12', lg: 'span 8' } }}>
          <ListCell
            addBorderBottom={true}
            icon={config.icon}
            primaryText={upperFirst(contactMethod)}
            secondaryText={`Find listings using your contact ${config.text.label}`}
            ctaIcon={false}
            cta={
              <FormField css={{ marginTop: '400' }} error={Boolean(error)}>
                <LabeledInput
                  label={<Label>Enter {config.text.label}</Label>}
                  input={
                    <Input
                      data-testid="contact-method-input"
                      fluid={false}
                      onChange={onChangeHandler}
                      onBlur={onBlurHandler}
                      onFocus={onFocusHandler}
                      onKeyDown={onKeyDownHandler}
                      type={config.type as 'tel' | 'email'}
                      value={
                        contactMethod === 'phone' ? numberUtils.formatPhoneNumberInput(contactValue) : contactValue
                      }
                      error={!!error}
                      className="fs-mask"
                      css={{
                        ...(error === DUPLICATE_CONTACT_METHOD_ERROR && {
                          caretColor: 'border.accent.green.hero',
                          '&, &[disabled], &:hover:hover, &:active:active, &:focus': {
                            borderColor: 'border.accent.green.hero',
                          },
                        }),
                      }}
                    />
                  }
                />
                {error && (
                  <FormHelp
                    error={!!error}
                    css={{
                      color: error === DUPLICATE_CONTACT_METHOD_ERROR ? 'green.600' : 'FormField.HelperArea.text.error',
                    }}
                  >
                    {error}
                  </FormHelp>
                )}
              </FormField>
            }
          />
        </Box>
      </Box>
      <Box css={{ textAlign: 'center', paddingTop: '800' }}>
        <Button tone="brand" emphasis="filled" disabled={!isValidContactMethodType || !!error} onClick={lookupListings}>
          Look up listings
        </Button>
        {propertyLookupError && <PropertyLookupError />}
      </Box>
    </>
  );
};

export default ContactMethodLookup;
